import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Line, Doughnut } from 'react-chartjs-2';
// eslint-disable-next-line no-unused-vars
import Chart from 'chart.js/auto';
import Loader from '../../../Components/utils/Loader';
import { CSVLink } from 'react-csv';
import { FiDownload } from 'react-icons/fi';
import { exportToPDF } from '../../../utils/exportUtils';

const API = process.env.REACT_APP_Backend_Host_Name;

const STATUS_COLORS = {
    'Active': '#0ea5e9', 'Completed': '#10b981', 'Cancelled': '#ef4444', 'Out for Delivery': '#6366f1',
    'Delivered': '#8b5cf6', 'Ready for Pickup': '#f59e0b', 'Picked Up': '#14b8a6', 'OnHold': '#f97316',
    'InDispute': '#ec4899', 'Resolved': '#64748b', 'Past Due': '#dc2626'
};

function AnalyticsOrders({ query }) {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const token = localStorage.getItem('adminToken');

    useEffect(() => {
        setLoading(true);
        axios.get(`${API}/api/v1/settings/admin/analytics/orders?${query}`, { headers: { Authorization: `Admin ${token}` } })
            .then(res => setData(res.data))
            .finally(() => setLoading(false));
    }, [query, token]);

    if (loading) return <Loader type="simpleMax" />;
    if (!data) return null;

    const chartOpts = { responsive: true, maintainAspectRatio: false, interaction: { mode: 'index', intersect: false }, plugins: { legend: { position: 'bottom', labels: { padding: 15, usePointStyle: true, pointStyle: 'circle', font: { size: 11 } } } }, scales: { x: { grid: { display: false }, ticks: { font: { size: 10 }, maxTicksLimit: 12 } }, y: { grid: { color: '#f0f0f0' }, ticks: { font: { size: 10 } }, beginAtZero: true } } };
    const doughnutOpts = { responsive: true, maintainAspectRatio: false, plugins: { legend: { position: 'bottom', labels: { padding: 12, usePointStyle: true, pointStyle: 'circle', font: { size: 11 } } } }, cutout: '65%' };

    const ordersChart = {
        labels: data.ordersOverTime.map(d => d.date),
        datasets: [
            { label: 'Orders Placed', data: data.ordersOverTime.map(d => d.count), borderColor: '#0ea5e9', backgroundColor: 'rgba(14,165,233,0.08)', fill: true, tension: 0.4, pointRadius: 2 },
            { label: 'Completed', data: data.completedOverTime.map(d => d.count), borderColor: '#10b981', backgroundColor: 'rgba(16,185,129,0.08)', fill: true, tension: 0.4, pointRadius: 2 },
        ]
    };

    const statusChart = {
        labels: data.statusDistribution.map(s => s.status),
        datasets: [{ data: data.statusDistribution.map(s => s.count), backgroundColor: data.statusDistribution.map(s => STATUS_COLORS[s.status] || '#94a3b8'), borderWidth: 0 }]
    };

    const b2cB2bChart = {
        labels: ['B2C', 'B2B'],
        datasets: [{ data: [data.b2cVsB2b.b2c, data.b2cVsB2b.b2b], backgroundColor: ['#6366f1', '#10b981'], borderWidth: 0 }]
    };

    const delPickChart = {
        labels: ['Delivery', 'Pickup'],
        datasets: [{ data: [data.deliveryVsPickup.delivery, data.deliveryVsPickup.pickup], backgroundColor: ['#0ea5e9', '#f59e0b'], borderWidth: 0 }]
    };

    const avgChart = {
        labels: data.avgValueOverTime.map(d => d.date),
        datasets: [{ label: 'Avg Order Value ($)', data: data.avgValueOverTime.map(d => d.avgValue), borderColor: '#ec4899', backgroundColor: 'rgba(236,72,153,0.08)', fill: true, tension: 0.4, pointRadius: 2 }]
    };

    const csvData = data ? [
        { Metric: 'Total Orders', Value: data.b2cVsB2b.b2c + data.b2cVsB2b.b2b, Extra: '' },
        { Metric: 'Completed Orders', Value: data.statusDistribution.find(s => s.status === 'Completed')?.count || 0, Extra: '' },
        { Metric: 'Cancelled Orders', Value: data.cancelledCount, Extra: '' },
        { Metric: 'Total Disputes', Value: data.disputeStats.reduce((s, d) => s + d.count, 0), Extra: '' },
        { Metric: '', Value: '', Extra: '' },
        { Metric: 'ORDER STATUS DISTRIBUTION', Value: '', Extra: '' },
        ...data.statusDistribution.map(s => ({ Metric: s.status, Value: s.count, Extra: '' })),
        { Metric: '', Value: '', Extra: '' },
        { Metric: 'CUSTOMER TYPES', Value: '', Extra: '' },
        { Metric: 'B2C Orders', Value: data.b2cVsB2b.b2c, Extra: '' },
        { Metric: 'B2B Orders', Value: data.b2cVsB2b.b2b, Extra: '' },
        { Metric: '', Value: '', Extra: '' },
        { Metric: 'DELIVERY TYPES', Value: '', Extra: '' },
        { Metric: 'Delivery', Value: data.deliveryVsPickup.delivery, Extra: '' },
        { Metric: 'Pickup', Value: data.deliveryVsPickup.pickup, Extra: '' },
        { Metric: '', Value: '', Extra: '' },
        { Metric: 'TOP SELLERS', Value: '', Extra: '' },
        { Metric: 'Seller Name', Value: 'Orders', Extra: 'Revenue' },
        ...data.topSellers.map(s => ({ Metric: s.sellerName || 'N/A', Value: s.orderCount, Extra: `$${s.totalRevenue?.toFixed(2) || 0}` })),
        { Metric: '', Value: '', Extra: '' },
        { Metric: 'ORDERS OVER TIME', Value: '', Extra: '' },
        { Metric: 'Date', Value: 'Orders Placed', Extra: 'Completed' },
        ...data.ordersOverTime.map((d, i) => ({
            Metric: d.date,
            Value: d.count,
            Extra: data.completedOverTime[i]?.count || 0
        }))
    ] : [];

    return (
        <div className="analyticsOrdersTab" id="analytics-orders-report">
            <div className="analyticsExportBar">
                <CSVLink data={csvData} filename="orders_report.csv" className="exportBtn csvBtn">
                    <FiDownload /> Export CSV
                </CSVLink>
                <button onClick={() => exportToPDF('analytics-orders-report', 'orders_report')} className="exportBtn pdfBtn">
                    <FiDownload /> Export PDF
                </button>
            </div>

            <div className="analyticsSimpleStats fourCol">
                <div className="simpleStat"><div className="simpleStatValue" style={{ color: '#0ea5e9' }}>{data.b2cVsB2b.b2c + data.b2cVsB2b.b2b}</div><div className="simpleStatLabel">Total Orders</div></div>
                <div className="simpleStat"><div className="simpleStatValue" style={{ color: '#10b981' }}>{data.statusDistribution.find(s => s.status === 'Completed')?.count || 0}</div><div className="simpleStatLabel">Completed</div></div>
                <div className="simpleStat"><div className="simpleStatValue" style={{ color: '#ef4444' }}>{data.cancelledCount}</div><div className="simpleStatLabel">Cancelled (Period)</div></div>
                <div className="simpleStat"><div className="simpleStatValue" style={{ color: '#f97316' }}>{data.disputeStats.reduce((s, d) => s + d.count, 0)}</div><div className="simpleStatLabel">Disputes (Period)</div></div>
            </div>

            <div className="analyticsChartCard">
                <h3 className="chartCardTitle">Orders Over Time</h3>
                <div className="chartContainer" style={{ height: 300 }}><Line data={ordersChart} options={chartOpts} /></div>
            </div>

            <div className="analyticsGridRow three">
                <div className="analyticsChartCard">
                    <h3 className="chartCardTitle">Order Status Distribution</h3>
                    <div className="chartContainer" style={{ height: 240 }}><Doughnut data={statusChart} options={doughnutOpts} /></div>
                </div>
                <div className="analyticsChartCard">
                    <h3 className="chartCardTitle">B2C vs B2B</h3>
                    <div className="chartContainer" style={{ height: 240 }}><Doughnut data={b2cB2bChart} options={doughnutOpts} /></div>
                    <div className="chartLegendSummary">
                        <span className="legendDot" style={{ background: '#6366f1' }}></span> {data.b2cVsB2b.b2c} B2C
                        <span className="legendDot" style={{ background: '#10b981', marginLeft: 12 }}></span> {data.b2cVsB2b.b2b} B2B
                    </div>
                </div>
                <div className="analyticsChartCard">
                    <h3 className="chartCardTitle">Delivery vs Pickup</h3>
                    <div className="chartContainer" style={{ height: 240 }}><Doughnut data={delPickChart} options={doughnutOpts} /></div>
                    <div className="chartLegendSummary">
                        <span className="legendDot" style={{ background: '#0ea5e9' }}></span> {data.deliveryVsPickup.delivery} Delivery
                        <span className="legendDot" style={{ background: '#f59e0b', marginLeft: 12 }}></span> {data.deliveryVsPickup.pickup} Pickup
                    </div>
                </div>
            </div>

            <div className="analyticsChartCard">
                <h3 className="chartCardTitle">Average Order Value Trend</h3>
                <div className="chartContainer" style={{ height: 260 }}><Line data={avgChart} options={chartOpts} /></div>
            </div>

            {data.topSellers.length > 0 && (
                <div className="analyticsChartCard">
                    <h3 className="chartCardTitle">Top Sellers by Orders</h3>
                    <div className="analyticsTableWrap">
                        <table className="analyticsTable">
                            <thead><tr><th>#</th><th>Seller</th><th>Orders</th><th>Revenue</th></tr></thead>
                            <tbody>
                                {data.topSellers.map((s, i) => (
                                    <tr key={i}>
                                        <td>{i + 1}</td>
                                        <td className="sellerCell">
                                            {s.profileImage && <img src={s.profileImage} alt="" className="sellerThumb" />}
                                            <span>{s.sellerName || 'N/A'}</span>
                                        </td>
                                        <td>{s.orderCount}</td>
                                        <td>${s.totalRevenue?.toFixed(2)}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}
        </div>
    );
}

export default AnalyticsOrders;
